import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { errMsg } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

const LowScoreDialog = ({ msg, onSubmit, onClose }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) return toast.error("Please give a reason of at least 5 characters");
    setBusy(true);
    await onSubmit(reason.trim());
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="!bg-[#121824] border-rose-500/40 max-w-lg" data-testid="low-score-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><AlertTriangle size={18} className="text-rose-400" />Low contractor score</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-3">
          <p className="text-sm text-rose-200" data-testid="low-score-message">{msg}</p>
          <label className="grid gap-1.5"><span className="text-xs text-slate-400">Reason for going ahead *</span>
            <textarea className="c-input min-h-[80px]" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Filing backlog cleared in October; PF dues paid in full" data-testid="low-score-reason" /></label>
          <p className="text-[11px] text-slate-500">The reason is saved with your name and the score at this moment.</p>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy} className="bg-rose-500 hover:bg-rose-400 text-white font-semibold" data-testid="low-score-confirm">Continue anyway</Button>
            <Button type="button" variant="outline" className="border-[#1F2A3E]" onClick={onClose} data-testid="low-score-cancel">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>);
};

const isLow = (e) => e?.response?.status === 409 && String(e?.response?.data?.detail || "").startsWith("LOW_SCORE");

// run(fn): calls fn(null); if the server warns about a low score, asks for a reason and calls fn(reason). Resolves true on success.
export const useScoreGate = () => {
  const [pending, setPending] = useState(null);
  const run = async (fn) => {
    try { await fn(null); return true; } catch (e) {
      if (isLow(e)) return new Promise((resolve) => setPending({ msg: e.response.data.detail.replace(/^LOW_SCORE:\s*/, ""), fn, resolve }));
      toast.error(errMsg(e)); return false;
    }
  };
  const close = (ok) => { pending?.resolve(ok); setPending(null); };
  const dialog = pending && <LowScoreDialog msg={pending.msg} onClose={() => close(false)}
    onSubmit={async (r) => { try { await pending.fn(r); close(true); } catch (e) { toast.error(errMsg(e)); } }} />;
  return { run, dialog };
};
